/**
 * Domínios fechados do leiaute da NF-e usados na matriz tributária: código + descrição.
 * São a MESMA lista que valida os parâmetros (os enums de `fiscal.ts` são derivados daqui) e
 * um teste confere que o banco (`app.tax_param_spec`, migration 0012) aceita exatamente os
 * mesmos códigos. Nenhum valor de alíquota ou regra: só os códigos e o que significam.
 *
 * Fica sem lista fechada o que não tem tabela estável na arquitetura: CST e cClassTrib do
 * IBS/CBS (tabela da reforma ainda em mudança), enquadramento do IPI e natureza da receita
 * de PIS/COFINS — esses continuam como texto, com o formato na dica.
 */

export type DomainOption = { code: string; label: string };

/** CST do ICMS (2 dígitos; a origem da mercadoria é um campo separado). */
export const ICMS_CST: readonly DomainOption[] = [
  { code: "00", label: "Tributada integralmente" },
  { code: "10", label: "Tributada e com cobrança do ICMS por substituição tributária" },
  { code: "20", label: "Com redução de base de cálculo" },
  {
    code: "30",
    label: "Isenta ou não tributada e com cobrança do ICMS por substituição tributária",
  },
  { code: "40", label: "Isenta" },
  { code: "41", label: "Não tributada" },
  { code: "50", label: "Suspensão" },
  { code: "51", label: "Diferimento" },
  { code: "60", label: "ICMS cobrado anteriormente por substituição tributária" },
  { code: "61", label: "Tributação monofásica sobre combustíveis cobrada anteriormente" },
  {
    code: "70",
    label: "Com redução de base de cálculo e cobrança do ICMS por substituição tributária",
  },
  { code: "90", label: "Outras" },
];

/** CSOSN (Simples Nacional). */
export const ICMS_CSOSN: readonly DomainOption[] = [
  { code: "101", label: "Tributada pelo Simples Nacional com permissão de crédito" },
  { code: "102", label: "Tributada pelo Simples Nacional sem permissão de crédito" },
  { code: "103", label: "Isenção do ICMS no Simples Nacional para faixa de receita bruta" },
  {
    code: "201",
    label: "Tributada pelo Simples Nacional com permissão de crédito e com cobrança do ICMS-ST",
  },
  {
    code: "202",
    label: "Tributada pelo Simples Nacional sem permissão de crédito e com cobrança do ICMS-ST",
  },
  {
    code: "203",
    label:
      "Isenção do ICMS no Simples Nacional para faixa de receita bruta e com cobrança do ICMS-ST",
  },
  { code: "300", label: "Imune" },
  { code: "400", label: "Não tributada pelo Simples Nacional" },
  { code: "500", label: "ICMS cobrado anteriormente por substituição tributária ou antecipação" },
  { code: "900", label: "Outros" },
];

/** Modalidade de determinação da base de cálculo do ICMS (modBC). */
export const ICMS_MOD_BC: readonly DomainOption[] = [
  { code: "0", label: "Margem de valor agregado (%)" },
  { code: "1", label: "Pauta (valor)" },
  { code: "2", label: "Preço tabelado máximo (valor)" },
  { code: "3", label: "Valor da operação" },
];

/** Modalidade de determinação da base de cálculo do ICMS-ST (modBCST). */
export const ICMS_MOD_BC_ST: readonly DomainOption[] = [
  { code: "0", label: "Preço tabelado ou máximo sugerido" },
  { code: "1", label: "Lista negativa (valor)" },
  { code: "2", label: "Lista positiva (valor)" },
  { code: "3", label: "Lista neutra (valor)" },
  { code: "4", label: "Margem de valor agregado (%)" },
  { code: "5", label: "Pauta (valor)" },
  { code: "6", label: "Valor da operação" },
];

/** Motivo da desoneração do ICMS (motDesICMS). */
export const ICMS_MOT_DES: readonly DomainOption[] = [
  { code: "1", label: "Táxi" },
  { code: "3", label: "Produtor agropecuário" },
  { code: "4", label: "Frotista / locadora" },
  { code: "5", label: "Diplomático / consular" },
  {
    code: "6",
    label: "Utilitários e motocicletas da Amazônia Ocidental e Áreas de Livre Comércio",
  },
  { code: "7", label: "SUFRAMA" },
  { code: "8", label: "Venda a órgão público" },
  { code: "9", label: "Outros" },
  { code: "10", label: "Deficiente condutor" },
  { code: "11", label: "Deficiente não condutor" },
  { code: "12", label: "Órgão de fomento e desenvolvimento agropecuário" },
  { code: "16", label: "Olimpíadas Rio 2016" },
  { code: "90", label: "Solicitado pelo Fisco" },
];

/** CST do IPI. */
export const IPI_CST: readonly DomainOption[] = [
  { code: "00", label: "Entrada com recuperação de crédito" },
  { code: "01", label: "Entrada tributada com alíquota zero" },
  { code: "02", label: "Entrada isenta" },
  { code: "03", label: "Entrada não tributada" },
  { code: "04", label: "Entrada imune" },
  { code: "05", label: "Entrada com suspensão" },
  { code: "49", label: "Outras entradas" },
  { code: "50", label: "Saída tributada" },
  { code: "51", label: "Saída tributada com alíquota zero" },
  { code: "52", label: "Saída isenta" },
  { code: "53", label: "Saída não tributada" },
  { code: "54", label: "Saída imune" },
  { code: "55", label: "Saída com suspensão" },
  { code: "99", label: "Outras saídas" },
];

/** CST de PIS e de COFINS (mesma tabela). */
export const PIS_COFINS_CST: readonly DomainOption[] = [
  { code: "01", label: "Operação tributável com alíquota básica" },
  { code: "02", label: "Operação tributável com alíquota diferenciada" },
  { code: "03", label: "Operação tributável com alíquota por unidade de medida de produto" },
  { code: "04", label: "Operação tributável monofásica — revenda a alíquota zero" },
  { code: "05", label: "Operação tributável por substituição tributária" },
  { code: "06", label: "Operação tributável a alíquota zero" },
  { code: "07", label: "Operação isenta da contribuição" },
  { code: "08", label: "Operação sem incidência da contribuição" },
  { code: "09", label: "Operação com suspensão da contribuição" },
  { code: "49", label: "Outras operações de saída" },
  {
    code: "50",
    label: "Crédito — vinculada exclusivamente a receita tributada no mercado interno",
  },
  {
    code: "51",
    label: "Crédito — vinculada exclusivamente a receita não tributada no mercado interno",
  },
  { code: "52", label: "Crédito — vinculada exclusivamente a receita de exportação" },
  {
    code: "53",
    label: "Crédito — vinculada a receitas tributadas e não tributadas no mercado interno",
  },
  {
    code: "54",
    label: "Crédito — vinculada a receitas tributadas no mercado interno e de exportação",
  },
  {
    code: "55",
    label: "Crédito — vinculada a receitas não tributadas no mercado interno e de exportação",
  },
  {
    code: "56",
    label:
      "Crédito — vinculada a receitas tributadas e não tributadas no mercado interno e de exportação",
  },
  {
    code: "60",
    label:
      "Crédito presumido — aquisição vinculada exclusivamente a receita tributada no mercado interno",
  },
  {
    code: "61",
    label:
      "Crédito presumido — aquisição vinculada exclusivamente a receita não tributada no mercado interno",
  },
  {
    code: "62",
    label: "Crédito presumido — aquisição vinculada exclusivamente a receita de exportação",
  },
  {
    code: "63",
    label:
      "Crédito presumido — aquisição vinculada a receitas tributadas e não tributadas no mercado interno",
  },
  {
    code: "64",
    label:
      "Crédito presumido — aquisição vinculada a receitas tributadas no mercado interno e de exportação",
  },
  {
    code: "65",
    label:
      "Crédito presumido — aquisição vinculada a receitas não tributadas no mercado interno e de exportação",
  },
  {
    code: "66",
    label:
      "Crédito presumido — aquisição vinculada a receitas tributadas e não tributadas no mercado interno e de exportação",
  },
  { code: "67", label: "Crédito presumido — outras operações" },
  { code: "70", label: "Operação de aquisição sem direito a crédito" },
  { code: "71", label: "Operação de aquisição com isenção" },
  { code: "72", label: "Operação de aquisição com suspensão" },
  { code: "73", label: "Operação de aquisição a alíquota zero" },
  { code: "74", label: "Operação de aquisição sem incidência da contribuição" },
  { code: "75", label: "Operação de aquisição por substituição tributária" },
  { code: "98", label: "Outras operações de entrada" },
  { code: "99", label: "Outras operações" },
];

/** Os códigos de uma lista, no formato que `z.enum` espera. */
export function domainCodes(list: readonly DomainOption[]): [string, ...string[]] {
  const codes = list.map((o) => o.code);
  return [codes[0]!, ...codes.slice(1)];
}

/** "20 — Com redução de base de cálculo" */
export const domainLabel = (o: DomainOption) => `${o.code} — ${o.label}`;
